import './NumberFilterEditor.less';
import React from "react";
import {translate} from "../../../../i18n";
import typedOperators from "../../config/typedOperators";
import {FilterEditorTemplate} from "../../templates/filter-editor/FilterEditorTemplate";


let autoIncId = 0;


export default class NumberFilterEditor extends React.Component {
    constructor(props) {
        super(props);

        const operator = props.defaultOperator ? props.defaultOperator : this.getOperators()[0];

        /** @type {LabelMap} */
        const vSelectableOptions = props.vSelectableOptions;

        // use string to save value, convert to number when submit
        const value = this.isSelectInput(operator)
            ? (vSelectableOptions.get(props.defaultValue) ? props.defaultValue : null)
            : (
                this.isRangeInput(operator)
                    ? (props.defaultValue ? props.defaultValue.map(v => String(v)) : ['', ''])
                    : (
                        this.isListInput(operator)
                            ? (props.defaultValue ? props.defaultValue.map(v => String(v)) : [])
                            : (props.defaultValue || props.defaultValue === 0 ? String(props.defaultValue) : '')
                    )
            );

        this.state = {
            operator: operator,
            value: value,
            error: null,
            inputKey: ++autoIncId
        };
    }

    componentDidMount() {
        this.validate();
    }

    isRangeInput = (operator) => {
        return ['in_range'].includes(operator);
    };

    isListInput = (operator) => {
        return ['in_list'].includes(operator);
    };

    //deprecated
    isSelectInput = (operator) => {
        /** @type {LabelMap} */
        const vSelectableOptions = this.props.vSelectableOptions;

        return ['is'].includes(operator) && !!vSelectableOptions;
    };

    isNullable = (operator) => {
        return ['=', '!='].includes(operator);
    }

    getOperators = () => {
        const {vDecimalDigits, vSelectableOptions} = this.props;

        return typedOperators.number.filter(operator => {
            if (vSelectableOptions === undefined) {
                if (['is'].includes(operator)) {
                    return false;
                }
            }
            if (vDecimalDigits !== undefined) {
                // we cannot support != and in_list operator for decimal number, caused by API does not have OR operator
                if (['!=', 'in_list'].includes(operator)) {
                    return false;
                }
            }
            return true;
        });
    };

    setOperator = (newOperator) => {
        /** @type {LabelMap} */
        const vSelectableOptions = this.props.vSelectableOptions;

        const {operator, value} = this.state;
        let newValue = value;

        if (this.isSelectInput(newOperator)) {
            newValue = vSelectableOptions.items[0].value;
        } else if (this.isRangeInput(newOperator)) {
            if (this.isSelectInput(operator)) {
                newValue = ['', ''];
            } else if (this.isListInput(operator)) {
                newValue = [
                    value[0] !== undefined ? value[0] : '',
                    value[1] !== undefined ? value[1] : ''
                ];
            } else if (!this.isRangeInput(operator)) {
                newValue = [value, ''];
            }
        } else if (this.isListInput(newOperator)) {
            if (this.isSelectInput(operator)) {
                newValue = [];
            } else if (this.isRangeInput(operator)) {
                if (value[0] === value[1]) {
                    newValue = [value[0]];
                } else {
                    newValue = value;
                }
            } else if (!this.isListInput(operator)) {
                newValue = value.split(',');
            }
            newValue = newValue.filter(v => v !== '');
        } else {
            if (this.isSelectInput(operator)) {
                newValue = '';
            } else if (this.isRangeInput(operator)) {
                newValue = value[0];
            } else if (this.isListInput(operator)) {
                newValue = value[0] !== undefined ? value[0] : '';
            }
        }

        this.setState({
            operator: newOperator,
            value: newValue,
            inputKey: ++autoIncId
        }, () => {
            this.validate();
        });
    };

    setValue = (value) => {
        const {operator} = this.state;
        const {vDecimalDigits} = this.props;
        const isInt = vDecimalDigits === undefined;

        const correctTypo = v => {
            if (isInt) {
                return v.replace(/ /g, '');
            } else {
                return v
                .replace(/,/g, '.') // consider comma as dot to expresses decimal
                .replace(/ /g, '');
            }
        };
        if (this.isListInput(operator)) {
            // with list input
            // if value contains from 2 elements, if last element is empty, it will be deleted from value
            // this allows user to enter trailing comma without error
            if (value.length > 1 && value[value.length - 1].trim() === '') {
                value.pop();
            }
        }
        if (this.isSelectInput(operator)) {
            // do nothing
        } else if (this.isRangeInput(operator)) {
            value = value.map(v => correctTypo(v));
        } else if (this.isListInput(operator)) {
            value = value.map(v => correctTypo(v)).filter(v => v !== '');
        } else {
            value = correctTypo(value);
        }
        this.setState({
            value: value
        }, () => {
            this.validate();
        });
    };

    validate = () => {
        const {operator, value} = this.state;
        const {vMin, vMax, vDecimalDigits} = this.props;

        const isInt = vDecimalDigits === undefined;

        const errEmpty = value => !this.isNullable(operator) ? value === '' : false;
        const errNotInt = value => value !== '' && isInt && !/^-?[0-9]+$/.test(value);
        const errNotNumber = value => value !== '' && !isInt && isNaN(value);
        const errLtMin = value => value !== '' && undefined !== vMin && Number(value) < vMin;
        const errGtMax = value => value !== '' && undefined !== vMax && Number(value) > vMax;
        const errDecimalDigitsExceeded = value => value !== '' && undefined !== vDecimalDigits
            && value.split('.')[1] && value.split('.')[1].length > vDecimalDigits;
        const errInvalidRange = (x, y) => Number(x) >= Number(y);
        const errListEmpty = value => value.length === 0;
        const errListHasDuplicates = value => {
            const obj = {};
            value.forEach(v => {
                obj[v] = 1;
            });
            return Object.keys(obj).length !== value.length;
        };

        if (this.isSelectInput(operator)) {
            this.setState({
                error: null
            });
        } else if (this.isRangeInput(operator) || this.isListInput(operator)) {
            if (value.some(v => errEmpty(v)) || (this.isListInput(operator) && errListEmpty(value))) {
                this.setState({
                    error: isInt ? translate('Enter integers') : translate('Enter numbers')
                });
            } else if (value.some(v => errNotInt(v))) {
                this.setState({
                    error: translate('Enter integers')
                });
            } else if (value.some(v => errNotNumber(v))) {
                this.setState({
                    error: translate('Enter numbers')
                });
            } else if (value.some(v => errLtMin(v))) {
                this.setState({
                    error: translate('Min: ::number', {number: vMin})
                });
            } else if (value.some(v => errGtMax(v))) {
                this.setState({
                    error: translate('Max: ::number', {number: vMax})
                });
            } else if (value.some(v => errDecimalDigitsExceeded(v))) {
                this.setState({
                    error: translate('Number of decimal digits: ::number', {number: vDecimalDigits})
                });
            } else if (this.isRangeInput(operator) && errInvalidRange(value[0], value[1])) {
                this.setState({
                    error: translate('Invalid range')
                });
            } else if (this.isListInput(operator) && errListHasDuplicates(value)) {
                this.setState({
                    error: translate('List has duplicates')
                })
            } else {
                this.setState({
                    error: null
                });
            }
        } else {
            if (errEmpty(value)) {
                this.setState({
                    error: isInt ? translate('Enter an integer') : translate('Enter a number')
                });
            } else if (errNotInt(value)) {
                this.setState({
                    error: translate('Enter an integer')
                });
            } else if (errNotNumber(value)) {
                this.setState({
                    error: translate('Enter a number')
                });
            } else if (errLtMin(value)) {
                this.setState({
                    error: translate('Min: ::number', {number: vMin})
                });
            } else if (errGtMax(value)) {
                this.setState({
                    error: translate('Max: ::number', {number: vMax})
                });
            } else if (errDecimalDigitsExceeded(value)) {
                this.setState({
                    error: translate('Number of decimal digits: ::number', {number: vDecimalDigits})
                });
            } else {
                this.setState({
                    error: null
                });
            }
        }
    };

    submit = () => {
        const {update} = this.props;
        const {operator, value, error} = this.state;

        if (error !== null) {
            return;
        }

        const parsedValue = this.isSelectInput(operator)
            ? value
            : (
                this.isRangeInput(operator) || this.isListInput(operator)
                    ? value.map(v => Number(v))
                    : (value === '' ? null : Number(value))
            );
        update(operator, parsedValue);
    };

    renderInputComponentContent = () => {
        /** @type {LabelMap} */
        const vSelectableOptions = this.props.vSelectableOptions;
        const {operator, value} = this.state;
        const isSelectInput = this.isSelectInput(operator);
        const isRangeInput = this.isRangeInput(operator);
        const isListInput = this.isListInput(operator);

        if (isSelectInput) {
            return <select
                defaultValue={Math.max(0, vSelectableOptions.items.map(t => t.value).indexOf(value))}
                onChange={(event) => this.setValue(vSelectableOptions.items[Number(event.target.value)].value)}
            >
                {
                    vSelectableOptions.items.map((option, index) => {
                        return (
                            <option
                                key={index}
                                value={index}
                            >
                                {option.label}
                            </option>
                        )
                    })
                }
            </select>;
        }

        const onKeyDown = (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                this.submit();
            }
        };

        if (isRangeInput) {
            return <>
                <input
                    type="text"
                    defaultValue={value[0]}
                    onKeyUp={(event) => this.setValue([event.target.value, value[1]])}
                    onPaste={(event) => this.setValue([event.target.value, value[1]])}
                    onChange={(event) => this.setValue([event.target.value, value[1]])}
                    onKeyDown={onKeyDown}
                />
                <input
                    type="text"
                    defaultValue={value[1]}
                    onKeyUp={(event) => this.setValue([value[0], event.target.value])}
                    onPaste={(event) => this.setValue([value[0], event.target.value])}
                    onChange={(event) => this.setValue([value[0], event.target.value])}
                    onKeyDown={onKeyDown}
                />
            </>;
        }

        if (isListInput) {
            return <input
                type="text"
                defaultValue={value.join(', ')}
                placeholder={'10, 200, 500'}
                onKeyUp={(event) => this.setValue(event.target.value.split(','))}
                onPaste={(event) => this.setValue(event.target.value.split(','))}
                onChange={(event) => this.setValue(event.target.value.split(','))}
                onKeyDown={onKeyDown}
            />;
        }

        return <input
            type="text"
            defaultValue={value}
            onKeyUp={(event) => this.setValue(event.target.value)}
            onPaste={(event) => this.setValue(event.target.value)}
            onChange={(event) => this.setValue(event.target.value)}
            onKeyDown={onKeyDown}
        />;
    };

    renderInputComponent = () => {
        const {operator, inputKey} = this.state;
        const isSelectInput = this.isSelectInput(operator);
        const isRangeInput = this.isRangeInput(operator);
        const isListInput = this.isListInput(operator);

        return <div
            key={inputKey}
            className={
                'value-input'
                + (isSelectInput ? ' is-select' : '')
                + (isRangeInput ? ' is-range' : '')
                + (isListInput ? ' is-list' : '')
            }
        >
            {this.renderInputComponentContent()}
        </div>;
    };

    render() {
        const {fieldName} = this.props;
        const {operator, error} = this.state;

        return <div className="number-filter-editor">
            <FilterEditorTemplate
                fieldName={fieldName}
                operator={operator}
                setOperator={this.setOperator}
                availableOperators={this.getOperators()}
                inputComponent={this.renderInputComponent()}
                error={error}
                apply={this.submit}
            />
        </div>;
    }
}
